import { useEffect } from "react";

import Link from "next/link";

import { apiUrls } from "config";
import flowSpecs from "config/flow-specs";

import Tag from "ui-components/Tag";

import { useGetUserDetails } from "auth/auth-state";
import { ShareEntity } from "lib/permissions";

import { useFetchProjectListing } from "modules/projects/api";

import Rename from "./Rename";
import Delete from "./Delete";
import DataFileSummary from "./Data-File-Summary";
import ContextSummary from "./Context-Summary";
import Button from "ui-components/Button";

const Projects = () => {
  const userDetails = useGetUserDetails();
  const [data, status, hasNextPage, getNextPage] = useFetchProjectListing();

  useEffect(() => {
    hasNextPage && getNextPage();
  }, [getNextPage, hasNextPage, data]);

  const listing = data.flat();

  const empty = status !== "loading" && listing.length === 0;

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
      <div className="pb-4 mb-8">
        <h1 className="h2 text-center">Existing Projects</h1>
      </div>
      {empty && (
        <div className="text-center">
          <p>
            <Link href="/recipes">
              <a>
                <Button>Create your First Project</Button>
              </a>
            </Link>
          </p>
        </div>
      )}
      {listing.length >= 1 && (
        <table className="table table-auto w-full">
          <thead>
            <tr>
              <th className="font-semibold text-left table-cell w-1/6">
                <p className="px-1 mx-1">Creation Details</p>
              </th>
              <th className="font-semibold text-left table-cell w-1/2">
                <p className="px-1 mx-1">Project</p>
              </th>
              <th className="font-semibold text-left table-cell">
                <p className="px-1 mx-1">Context</p>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.flat().map((d) => (
              <tr key={d.id} className="table-row border-t-2">
                <td className="table-cell">
                  <div className="m-1">
                    <p>
                      {new Date(d.created_time).toLocaleDateString(undefined, {
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true,
                      })}
                    </p>
                    <p>
                      {d.flow === "process-ops"
                        ? "unit-operations"
                        : flowSpecs.find((f) => f.link === d.flow)?.title ||
                          d.flow}
                    </p>
                  </div>
                </td>
                <td className="table-cell">
                  <p className="m-1">
                    <Link href={`/${d.flow}/${d.id}`}>
                      <a className="text-primary-main">
                        {d.name ? (
                          <span>
                            <span className="font-bold">{d.name}</span> ({d.id})
                          </span>
                        ) : (
                          <span className="font-bold">{d.id}</span>
                        )}
                      </a>
                    </Link>
                  </p>
                  {d.owner === userDetails?.email ? (
                    <div className="flex">
                      <div className="p-1">
                        <ShareEntity
                          entityId={d.id}
                          baseUrl={apiUrls.projects}
                          entityName="Project"
                          size="small"
                        />
                      </div>
                      <div className="p-1">
                        <Rename projectId={d.id} currentName={d.name} />
                      </div>
                      <div className="p-1">
                        <Delete projectId={d.id} />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap">
                      <Tag className="text-base">Shared by {d.owner}</Tag>
                    </div>
                  )}
                </td>
                <td className="table-cell">
                  <div className="m-1">
                    <DataFileSummary project={d} />
                    <ContextSummary project={d} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Projects;
